import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { extractGpsTrack } from "@/api/gpsTrack";
import { parseGpxMetadataTime } from "@shared/gpxTrack";
import { normalizeCity } from "@/lib/geo";
import { formatInTz } from "@/lib/datetime";
import { logger, serializeError } from "@/lib/logger";

const BUCKET = "gpx-tracks";

export type GpxParticipation =
  Database["public"]["Functions"]["list_my_gpx_participations"]["Returns"][number];

export interface GpxStart {
  /** ISO start time (first trackpoint's `<time>`, normalized to UTC). */
  time: string;
  /** Start coordinates, when the first trackpoint carries lat/lon. */
  lat: number | null;
  lon: number | null;
}

/**
 * Extract the track start point (time + coordinates) from raw GPX XML.
 *
 * The start is the EARLIEST trackpoint by time (the moment the runner pressed
 * start), not the first one in file order. Pause/resume exports write multiple
 * <trkseg>s and can carry points out of file order, so file-first and
 * earliest-by-time can differ by seconds/minutes — which would anchor the run
 * legs to the wrong instant and misalign every leg. Falls back to the
 * <metadata> stamp only when the export carries no timed trackpoint at all.
 * Throws when neither is present. Coordinates are best-effort (null when absent).
 */
export function extractGpxStartPoint(xml: string): GpxStart {
  // Earliest timed trackpoint = the moment the runner pressed start. Points are
  // scanned in file order then reduced to the minimum time, so a pause/resume
  // export whose segments are out of file order still anchors on the true start
  // (matching parseGpxPoints' time-sorted basis used by the metrics). Coords are
  // optional here (unlike parseGpxPoints, which drops uncoordinated points) so a
  // timed-but-coordless start still yields a usable anchor time.
  let best: {
    t: number;
    iso: string;
    lat: number | null;
    lon: number | null;
  } | null = null;
  const re = /<trkpt([^>]*)>([\s\S]*?)<\/trkpt>|<trkpt([^>]*)\/>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    const openAttrs = m[1] ?? m[3] ?? "";
    const body = m[2] ?? "";
    const timeText = body.match(/<time>\s*([^<]+?)\s*<\/time>/i)?.[1];
    if (!timeText) continue;
    const t = Date.parse(timeText.trim());
    if (Number.isNaN(t)) continue;
    if (best === null || t < best.t) {
      const lat = Number(openAttrs.match(/\blat\s*=\s*["']([^"']+)["']/i)?.[1]);
      const lon = Number(openAttrs.match(/\blon\s*=\s*["']([^"']+)["']/i)?.[1]);
      best = {
        t,
        iso: new Date(t).toISOString(),
        lat: Number.isFinite(lat) ? lat : null,
        lon: Number.isFinite(lon) ? lon : null,
      };
    }
  }
  if (best) {
    return { time: best.iso, lat: best.lat, lon: best.lon };
  }

  const metadataTime = parseGpxMetadataTime(xml);
  if (!metadataTime) {
    throw new Error(
      "This GPX file has no timestamps, so we can't work out your run time. " +
        "Export the track with time data enabled and try again.",
    );
  }
  return { time: metadataTime, lat: null, lon: null };
}

/** Extract just the ISO start time from raw GPX XML. */
export function extractGpxStartTime(xml: string): string {
  return extractGpxStartPoint(xml).time;
}

/** Read a GPX File and return its start point (time + coordinates). */
export async function parseGpxStart(file: File): Promise<GpxStart> {
  return extractGpxStartPoint(await file.text());
}

const GEOCODE_TIMEOUT_MS = 5000;

/** A city resolved from run coordinates, ready to anchor a virtual participation. */
interface GeocodedCity {
  city: string;
  country: string | null;
}

/**
 * Reverse-geocode a run's start point to a city via OpenStreetMap Nominatim.
 *
 * Runs in the browser on purpose: Nominatim blocks the datacenter IPs a Supabase
 * edge function calls from, so geocoding server-side reliably returns nothing and
 * the participation would stay Virtual. The user's own IP is served normally.
 *
 * Best-effort — null on any failure (bad coords, timeout, throttling, no city in
 * the response) so it can never break the upload it is attached to.
 */
async function reverseGeocodeCity(
  lat: number,
  lon: number,
): Promise<GeocodedCity | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&accept-language=en&lat=${lat}&lon=${lon}&zoom=14&addressdetails=1`,
      {
        headers: { Accept: "application/json" },
        signal: AbortSignal.timeout(GEOCODE_TIMEOUT_MS),
      },
    );
    if (!res.ok) return null;
    const data = await res.json();
    const a = data?.address;
    const city: string | undefined =
      a?.city ||
      a?.town ||
      a?.village ||
      a?.municipality ||
      a?.hamlet ||
      a?.county;
    if (!city) return null;
    return { city: normalizeCity(city), country: a?.country ?? null };
  } catch {
    return null;
  }
}

/**
 * Anchor a virtual participation to the city its track started in. Best-effort:
 * reverse-geocodes the start point in the browser and, when it resolves to a
 * city, asks the DB to move the participation there (creating the location, with
 * these coordinates, if it is new) so the runner shows up on /geography. Silent
 * no-op for onsite participations and when geocoding yields no city.
 */
async function anchorVirtualLocation(
  participationId: number,
  personId: number,
  start: GpxStart,
): Promise<void> {
  if (start.lat === null || start.lon === null) return;
  const place = await reverseGeocodeCity(start.lat, start.lon);
  if (!place) return;
  const { error } = await supabase.rpc(
    "set_virtual_participation_location" as never,
    {
      p_participation_id: participationId,
      p_person_id: personId,
      p_city: place.city,
      p_country: place.country,
      p_lat: start.lat,
      p_lon: start.lon,
    } as never,
  );
  if (error) {
    logger.error("Failed to anchor virtual participation location", {
      participationId,
      ...serializeError(error),
    });
  }
}

/** Participations the signed-in user can attach a GPX track to. */
export async function listMyGpxParticipations(): Promise<GpxParticipation[]> {
  const { data, error } = await supabase.rpc("list_my_gpx_participations");
  if (error) throw error;
  return data ?? [];
}

/**
 * Days after the quizrun closes during which a runner may still replace their
 * own track. Mirrors `gpx_replace_until` in SQL, which is what's enforced;
 * this copy is only for screens that don't fetch the server's value.
 */
const GPX_REPLACE_GRACE_DAYS = 2;

export function gpxReplaceUntil(quizrunDateFinish: string): string {
  const t = new Date(quizrunDateFinish).getTime();
  return new Date(t + GPX_REPLACE_GRACE_DAYS * 86_400_000).toISOString();
}

/**
 * Whether a runner may still replace their own existing track. A first upload
 * is always allowed; admins and hosts aren't bound by the deadline.
 */
export function canReplaceOwnGpx(
  replaceUntil: string | null | undefined,
): boolean {
  return replaceUntil == null || Date.now() <= new Date(replaceUntil).getTime();
}

/** "11 Oct, 22:00" — the replace deadline in the viewer's local time. */
export function formatGpxReplaceUntil(replaceUntil: string): string {
  return formatInTz(replaceUntil, null, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * Upload a GPX track for a participation member: parse its start time, store the
 * file in the private bucket at the canonical path, then record the link on the
 * member and recompute the participation's configured run legs server-side.
 *
 * By default the file replaces the member's whole track (its first file is
 * overwritten and any extra files are dropped). With `addPart` it is stored as
 * one more file of the same track — for a recording that was stopped and
 * restarted — and the analysis measures all files together.
 *
 * `personId` is the runner the track belongs to, not necessarily the caller: an
 * admin or the host of the participation's city may upload on their behalf.
 *
 * The path comes from the database rather than being built here, so the client,
 * the storage policies and the RPCs can never disagree about where a track
 * belongs.
 *
 * Finally kicks off the server-side track analysis. That's best-effort: the
 * upload itself has already succeeded by then, so a failure is reported as
 * nalysed: false (a host can recompute from the track page) rather than thrown.
 */
export async function uploadGpxTrack(
  participationId: number,
  personId: number,
  file: File,
  { addPart = false }: { addPart?: boolean } = {},
): Promise<GpxAnalysisOutcome> {
  const start = await parseGpxStart(file);
  const parts = await listGpxParts(participationId, personId);

  if (addPart) {
    const part = Math.max(1, ...parts.map((p) => p.part)) + 1;
    const { data: path, error: pathError } = await supabase.rpc(
      "gpx_part_object_path",
      {
        p_participation_id: participationId,
        p_person_id: personId,
        p_part: part,
      },
    );
    if (pathError) throw pathError;
    if (!path) throw new Error("Could not work out where to store this track.");

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, { upsert: true, contentType: "application/gpx+xml" });
    if (uploadError) throw uploadError;

    const { data: startsRun, error: rpcError } = await supabase.rpc(
      "add_participation_gpx_part",
      {
        p_participation_id: participationId,
        p_person_id: personId,
        p_gpx_start: start.time,
        p_file_link: path,
      },
    );
    if (rpcError) throw rpcError;

    // An earlier recording now starts the run: its city is where they ran.
    if (startsRun)
      await anchorVirtualLocation(participationId, personId, start);
  } else {
    const { data: path, error: pathError } = await supabase.rpc(
      "gpx_object_path",
      {
        p_participation_id: participationId,
        p_person_id: personId,
      },
    );
    if (pathError) throw pathError;
    if (!path) throw new Error("Could not work out where to store this track.");

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, { upsert: true, contentType: "application/gpx+xml" });
    if (uploadError) throw uploadError;

    const { error: rpcError } = await supabase.rpc("set_participation_gpx", {
      p_participation_id: participationId,
      p_person_id: personId,
      p_gpx_start: start.time,
      p_file_link: path,
    });
    if (rpcError) throw rpcError;

    // set_participation_gpx dropped the extra parts; their files go too.
    await removeGpxObjects(parts.map((p) => p.file_link));

    // Move a virtual participation to the city it ran in (best-effort).
    await anchorVirtualLocation(participationId, personId, start);
  }

  return analyse(participationId, personId);
}

/** Whether the track analysis ran after a track change. */
export interface GpxAnalysisOutcome {
  analysed: boolean;
}

async function analyse(
  participationId: number,
  personId: number,
): Promise<GpxAnalysisOutcome> {
  try {
    await extractGpsTrack(participationId, personId);
    return { analysed: true };
  } catch (err) {
    logger.error("GPX analysis failed", {
      participationId,
      ...serializeError(err),
    });
    return { analysed: false };
  }
}

export interface GpxPart {
  part: number;
  file_link: string;
  track_start: string;
  canonical_link: string;
}

/** A member's extra track files (part 2 onwards), in order. */
async function listGpxParts(
  participationId: number,
  personId: number,
): Promise<GpxPart[]> {
  const { data, error } = await supabase.rpc("list_member_gpx_parts", {
    p_participation_id: participationId,
    p_person_id: personId,
  });
  if (error) throw error;
  return data ?? [];
}

/** Best effort: a leftover object is unreferenced and harmless. */
async function removeGpxObjects(paths: string[]): Promise<void> {
  if (paths.length === 0) return;
  const { error } = await supabase.storage.from(BUCKET).remove(paths);
  if (error) {
    logger.warn("Failed to delete GPX part files", {
      paths,
      ...serializeError(error),
    });
  }
}

/**
 * Drop one extra file from a member's track, then re-analyse what's left.
 * The first file can only be replaced, not removed. A failed re-analysis is
 * reported as nalysed: false, not thrown: the file is already gone.
 */
export async function removeGpxPart(
  participationId: number,
  personId: number,
  fileLink: string,
): Promise<GpxAnalysisOutcome> {
  const { error } = await supabase.rpc("remove_participation_gpx_part", {
    p_participation_id: participationId,
    p_person_id: personId,
    p_file_link: fileLink,
  });
  if (error) throw error;
  await removeGpxObjects([fileLink]);
  return analyse(participationId, personId);
}

/**
 * Move GPX files whose stored path went stale in a Manage Participants save
 * (member moved to another participation, or renamed) to their canonical path,
 * then point the member's link at it. A member's extra track files follow.
 * Best effort: returns how many failed; a failed file stays at its old path
 * and keeps working for hosts.
 */
export async function relocateGpxTracks(
  relocations: Array<{
    participationId: number;
    personId: number;
    oldPath: string;
    newPath: string;
  }>,
): Promise<number> {
  let failed = 0;
  for (const r of relocations) {
    const { error: moveError } = await supabase.storage
      .from(BUCKET)
      .move(r.oldPath, r.newPath);
    // Relink even if the move failed: an earlier attempt may have moved the
    // file already. relink_member_gpx refuses when the new file isn't there.
    const { error } = await supabase.rpc("relink_member_gpx", {
      p_participation_id: r.participationId,
      p_person_id: r.personId,
      p_old_path: r.oldPath,
    });
    if (error) {
      logger.warn("GPX relocation failed", {
        ...r,
        ...serializeError(moveError ?? error),
      });
      failed++;
    }

    let parts: GpxPart[];
    try {
      parts = await listGpxParts(r.participationId, r.personId);
    } catch (err) {
      logger.warn("GPX part relocation failed", {
        ...r,
        ...serializeError(err),
      });
      failed++;
      continue;
    }
    for (const p of parts.filter((p) => p.file_link !== p.canonical_link)) {
      const { error: partMoveError } = await supabase.storage
        .from(BUCKET)
        .move(p.file_link, p.canonical_link);
      const { error: partError } = await supabase.rpc(
        "relink_member_gpx_part",
        {
          p_participation_id: r.participationId,
          p_person_id: r.personId,
          p_old_path: p.file_link,
        },
      );
      if (partError) {
        logger.warn("GPX part relocation failed", {
          ...p,
          ...serializeError(partMoveError ?? partError),
        });
        failed++;
      }
    }
  }
  return failed;
}
